import { SurveyType } from '@commutifi/models/Surveys'
import { queryClient } from 'api/reactQuery'
import { getAuthenticatedAccountId } from 'api/index'
import { getSubdomainQueryKey, IdentityProviderProps } from 'api/modules/subdomains'
import { FollowUpStorageKeys } from 'constants/onboarding'
import isNil from 'lodash/isNil'
import React from 'react'
import { useSelector } from 'react-redux'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import { Login, LoginProps } from 'shared/components/common/Login'
import { RequestResetPassword } from 'shared/components/common/Login/RequestResetPassword'
import { stateStorage } from 'shared/hooks/oauth/utils'
import { useMissingInformationModal } from 'shared/hooks/useMissingInformationModal'
import { CommonRouting, CommuterRouting, PublicPages, PublicPathsParams, PublicRouting } from 'src/routing'
import { selectors } from 'store/modules/enterpriseSurvey'
import storage from 'utils/storage'
import { ResetPassword } from './views/ResetPassword'
import { SignUp } from './views/SignUp'

export function Auth({
  history,
  location,
  match,
  canCreateAccount
}: AuthRoutingProps & { canCreateAccount?: boolean }) {
  const { openModal, renderModal } = useMissingInformationModal()
  const isProfileUpdateRequired: boolean | undefined = useSelector(
    selectors.makeGetPrefilledData('isProfileUpdateRequired')
  )

  const surveyType: SurveyType = useSelector(selectors.makeGetSurvey('type'))
  const isExpired: boolean = useSelector(selectors.makeGetPrefilledData('isExpired'))

  const identityProvider = queryClient.getQueryData<IdentityProviderProps>(getSubdomainQueryKey())

  // Store information to know if the user needs to answer some follow up survey
  // questions on his next dashboard access. That logic to get the proper survey
  // and show it to the user will be right before we show user home page (while loading state).
  // NOTE: We will same 'isProfileUpdateRequired' only on login since a new user won't need
  //       to update is profile
  React.useEffect(() => {
    if (isExpired) {
      return
    }

    if (match.params.shortlink) {
      storage.put(FollowUpStorageKeys.SurveyType, surveyType)
      storage.put(FollowUpStorageKeys.ShortlinkId, match.params.shortlink)
    }
    if (!isNil(isProfileUpdateRequired)) {
      storage.put(FollowUpStorageKeys.ProfileUpdate, Boolean(isProfileUpdateRequired))
    }
  }, [isExpired, isProfileUpdateRequired, match.params.shortlink, surveyType])

  // This part is needed to support auto-logging flow
  // with CBRE Host integration.
  // When the identityProvider.type is CBRE we generate a nonce manually
  // because the nonce value is used in the next step of the flow to retrieve the
  // identity provider id.
  // Then we redirect the user to the /sso/callback with all the parameters.
  React.useEffect(() => {
    async function processCbre() {
      if (identityProvider?.subdomain && identityProvider.type === 'CBRE') {
        const nonce = await stateStorage.saveState({
          targetUrl: location.pathname,
          identityProviderId: identityProvider.id,
          subdomain: identityProvider.subdomain
        })
        const queryParams =
          window.location.search === '' ? `?nonce=${nonce}` : `${window.location.search}&nonce=${nonce}`
        history.push(`${PublicRouting.oAuth.sso.path}${queryParams}`)
      }
    }
    processCbre()
  }, [location.pathname, history, identityProvider?.subdomain, identityProvider?.type, identityProvider?.id])

  React.useEffect(() => {
    if (getAuthenticatedAccountId()) {
      history.push(CommonRouting.select.path)
    }
  }, [history])

  const handleLogin: LoginProps['onLoginSuccess'] = (res) => {
    if (res?.redirectPath) {
      openModal({ redirectPath: res.redirectPath })
      return
    }

    // Resume to path, this is usually when the user is logged out for an expired token for example.
    // We want the user to resume on the page he was before the logout.
    if (location.state?.resumePathname) {
      return history.push(location.state.resumePathname)
    }

    return res.id && match.params.shortlink
      ? history.push(CommuterRouting.dashboard.path)
      : history.push(CommonRouting.select.path)
  }

  // Keep the location state to make sure if a resume pathname is defined, we will be able
  // to redirect the user back to the appropriate page.
  const handleForgotPassword: React.MouseEventHandler = () =>
    history.push({
      pathname: `auth/${PublicPages.ResetPassword}`,
      state: location.state
    })
  // See comment above to know why we keep the location state
  const handleCreateAccount: React.MouseEventHandler = () =>
    history.push({
      pathname: `auth/${PublicPages.CreateAccount}`,
      state: location.state
    })

  return (
    <>
      <Switch>
        <Route path={`${match.path}/${PublicPages.CreateAccount}`} component={SignUp} />
        <Route
          path={`${match.url}/${PublicPages.ResetPassword}/:token`}
          render={({ match }) => <ResetPassword token={match.params.token} />}
        />
        <Route path={`${match.url}/${PublicPages.ResetPassword}`} component={RequestResetPassword} />
        <Route
          path="*"
          render={(props: RouteComponentProps<{ [PublicPathsParams.Shortlink]: string }>) => (
            <Login
              oauthState={{ shortlinkId: props.match.params.shortlink, targetUrl: location.pathname }}
              canCreateAccount={canCreateAccount}
              onCreateAccount={handleCreateAccount}
              onForgotPassword={handleForgotPassword}
              onLoginSuccess={handleLogin}
              emailInputProps={{ autoFocus: true }}
            />
          )}
        />
      </Switch>
      {renderModal()}
    </>
  )
}

export type AuthRoutingProps = RouteComponentProps<
  { [PublicPathsParams.Shortlink]: string },
  never,
  { resumePathname?: string } | undefined
>
export default Auth
